import { useEffect, useState } from 'react';
import {
  useUpsertAnswer,
  type AuditAnswer,
  type AuditCriterion,
  type AuditEvidence,
  type AuditResponses,
  type CriterionPriority,
} from '../../lib/api/queries';
import { useDebouncedCallback } from '../../lib/useDebouncedCallback';
import { EvidenceStrip } from './EvidenceStrip';

const COMMENT_DEBOUNCE_MS = 800;

/** Score values shown as radios. -1 is "Not Applicable". */
const SCORE_OPTIONS: { value: number; label: string }[] = [
  { value: -1, label: 'N/A' },
  { value: 0,  label: '0' },
  { value: 1,  label: '1' },
  { value: 2,  label: '2' },
  { value: 3,  label: '3' },
  { value: 4,  label: '4' },
  { value: 5,  label: '5' },
];

/**
 * One criterion card: code + name + question + priority chip, then a row per
 * evidence item. Each item carries its own score radios and its own comment
 * textarea (matching the spreadsheet's per-row column F). Comments save on
 * debounce; score clicks save immediately.
 */
export function CriterionCard({
  tenantId,
  auditId,
  criterion,
  responses,
  evidence,
  editable,
}: {
  tenantId: string;
  auditId: string;
  criterion: AuditCriterion;
  responses: AuditResponses | undefined;
  /** All of the audit's evidence; filtered per item below. */
  evidence: AuditEvidence[] | undefined;
  editable: boolean;
}) {
  // Quick lookup of the cached answer for each item.
  const answersByItemId = new Map<string, AuditAnswer>();
  responses?.answers.forEach(a => answersByItemId.set(a.criterionItemId, a));
  const hiddenItemIds = new Set(responses?.hiddenItemIds ?? []);

  // Same shape for evidence, but many-per-item rather than one.
  const evidenceByItemId = new Map<string, AuditEvidence[]>();
  evidence?.forEach(e => {
    const existing = evidenceByItemId.get(e.criterionItemId);
    if (existing) existing.push(e);
    else evidenceByItemId.set(e.criterionItemId, [e]);
  });

  return (
    <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <header className="px-5 py-3 border-b border-gray-100 flex items-start gap-3 bg-gray-50">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900">
            <span className="text-gray-500 mr-2">{criterion.code}</span>
            {criterion.name}
          </h3>
          {criterion.question && (
            <p className="mt-1 text-sm text-gray-700">{criterion.question}</p>
          )}
        </div>
        <PriorityChip priority={criterion.priority} />
      </header>

      <ul className="divide-y divide-gray-100">
        {criterion.items.map(item => {
          // Conditional-hide: the server evaluates the rule (Slice 19a,
          // IApplicableItemResolver) and lists the hidden items on
          // /responses. Don't re-derive it here.
          if (hiddenItemIds.has(item.id)) {
            return null;
          }
          const existing = answersByItemId.get(item.id);
          return (
            <ItemRow
              key={item.id}
              tenantId={tenantId}
              auditId={auditId}
              itemId={item.id}
              itemText={item.text}
              score={existing?.score ?? null}
              comment={existing?.comment ?? null}
              evidence={evidenceByItemId.get(item.id) ?? EMPTY_EVIDENCE}
              editable={editable}
            />
          );
        })}
      </ul>
    </section>
  );
}

/** Stable identity so an item with no evidence doesn't remount its strip. */
const EMPTY_EVIDENCE: AuditEvidence[] = [];

function ItemRow({
  tenantId,
  auditId,
  itemId,
  itemText,
  score,
  comment,
  evidence,
  editable,
}: {
  tenantId: string;
  auditId: string;
  itemId: string;
  itemText: string;
  score: number | null;
  comment: string | null;
  evidence: AuditEvidence[];
  editable: boolean;
}) {
  const upsert = useUpsertAnswer(tenantId, auditId);

  // Mirror server comment into local state for debouncing. Re-sync when the
  // server's value changes (e.g. another user edited it under our nose, or
  // the response query revalidated).
  const [localComment, setLocalComment] = useState(comment ?? '');
  useEffect(() => { setLocalComment(comment ?? ''); }, [comment]);

  const saveComment = useDebouncedCallback(
    (text: string) =>
      upsert.mutate({ criterionItemId: itemId, comment: text.length > 0 ? text : null }),
    COMMENT_DEBOUNCE_MS,
  );

  const onScoreChange = (next: number) =>
    upsert.mutate({ criterionItemId: itemId, score: next });

  const onCommentChange = (text: string) => {
    setLocalComment(text);
    saveComment(text);
  };

  return (
    <li className="px-5 py-3">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col lg:flex-row lg:items-start gap-3">
          <p className="flex-1 text-sm text-gray-800">{itemText}</p>
          <ScoreRadios
            name={itemId}
            value={score}
            onChange={onScoreChange}
            disabled={!editable}
          />
        </div>
        <textarea
          value={localComment}
          onChange={e => onCommentChange(e.target.value)}
          disabled={!editable}
          placeholder="Comment / evidence reference (optional)"
          rows={1}
          className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700
                     focus:outline-none focus:ring-2 focus:ring-accent-alt-500 focus:border-transparent
                     disabled:bg-gray-50 disabled:cursor-not-allowed resize-y"
        />

        <EvidenceStrip
          tenantId={tenantId}
          auditId={auditId}
          criterionItemId={itemId}
          evidence={evidence}
          editable={editable}
        />
      </div>
    </li>
  );
}

function ScoreRadios({
  name, value, onChange, disabled,
}: {
  name: string; value: number | null; onChange: (v: number) => void; disabled: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="Score" className="flex gap-1 shrink-0">
      {SCORE_OPTIONS.map(opt => {
        const selected = value === opt.value;
        // Selected radio keeps its accent fill even when disabled so a
        // read-only viewer can still see which score was chosen. Disabled
        // just dims the saturation and removes the hover affordance.
        const cls = selected
          ? (disabled
              ? 'bg-accent-alt-600/70 border-accent-alt-600/70 text-white cursor-not-allowed'
              : 'bg-accent-alt-600 border-accent-alt-600 text-white cursor-pointer')
          : (disabled
              ? 'cursor-not-allowed opacity-60 border-gray-200 bg-gray-50 text-gray-500'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer');
        return (
          <label
            key={opt.value}
            className={
              'inline-flex items-center justify-center min-w-[2.25rem] px-2 py-1 text-sm rounded-md ' +
              'border transition-colors select-none ' + cls
            }
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={selected}
              onChange={() => onChange(opt.value)}
              disabled={disabled}
              className="sr-only"
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

function PriorityChip({ priority }: { priority: CriterionPriority }) {
  const cls =
    priority === 'High'   ? 'bg-danger-50 text-danger-500 border-danger-500/30' :
    priority === 'Medium' ? 'bg-warning-50 text-warning-500 border-warning-500/30' :
                            'bg-gray-100 text-gray-600 border-gray-300';
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`}>
      {priority} priority
    </span>
  );
}
